/** Money, percentages and labels for the partner screens. Money is in cents, in USD. */
import type { Tone } from '@/components/ui/StatusPill'
import type { CommissionStatus, CouponStatus, PartnerStatus, ReferralStatus } from '@/types/api'

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** 123450 → "$1,234.50". Missing values show as "$0.00". */
export function usd(cents: number | null | undefined): string {
  return USD.format((Number(cents) || 0) / 100)
}

/** Postgres numerics can arrive as strings; 30 → "30%", 12.5 → "12.5%". */
export function pct(value: number | string | null | undefined): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return '–'
  return `${Number.isInteger(n) ? n : n.toFixed(1).replace(/\.0$/, '')}%`
}

export const num = (value: number | string | null | undefined): number => Number(value) || 0

export const PARTNER_STATUS: Record<PartnerStatus, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'success' },
  suspended: { label: 'Suspended', tone: 'danger' },
}

export const COUPON_STATUS: Record<CouponStatus, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  active: { label: 'Active', tone: 'success' },
  inactive: { label: 'Inactive', tone: 'warning' },
}

export const COMMISSION_STATUS: Record<CommissionStatus, { label: string; tone: Tone }> = {
  pending: { label: 'To settle', tone: 'warning' },
  settled: { label: 'Settled', tone: 'success' },
  void: { label: 'Void', tone: 'neutral' },
}

export const REFERRAL_STATUS: Record<ReferralStatus, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'success' },
  past_due: { label: 'Past due', tone: 'warning' },
  canceled: { label: 'Canceled', tone: 'neutral' },
  unknown: { label: 'Unknown', tone: 'neutral' },
}

/** Today (or `date`) as YYYY-MM-DD in the admin's own time zone. */
export function isoDay(date = new Date()): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function daysAgo(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return isoDay(date)
}

/** The end of a YYYY-MM-DD day in local time, as an ISO instant. */
export function endOfDayIso(day: string): string {
  return new Date(`${day}T23:59:59.999`).toISOString()
}

/** A strong password: 16 characters, upper, lower, digit and symbol each guaranteed. */
export function generatePassword(length = 16): string {
  const sets = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnpqrstuvwxyz', '23456789', '!@#$%^&*-_=+?']
  const all = sets.join('')
  const pick = (chars: string) => {
    const buf = new Uint32Array(1)
    crypto.getRandomValues(buf)
    return chars[buf[0]! % chars.length]!
  }
  const out = sets.map(pick)
  while (out.length < length) out.push(pick(all))
  // Shuffle so the guaranteed characters are not always first.
  for (let i = out.length - 1; i > 0; i--) {
    const buf = new Uint32Array(1)
    crypto.getRandomValues(buf)
    const j = buf[0]! % (i + 1)
    ;[out[i], out[j]] = [out[j]!, out[i]!]
  }
  return out.join('')
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** The next free `P<n>` tag. */
export function suggestTag(tags: string[]): string {
  const used = tags.map((tag) => /^P(\d+)$/i.exec(tag.trim())?.[1]).filter(Boolean).map(Number)
  return `P${(used.length ? Math.max(...used) : 0) + 1}`
}
